import React, { useEffect, useState } from "react";
import "./index.scss";
import PageHeader from "../../../adminCompenents/listComponents/Layout";
import CardContainer from "../../../adminCompenents/listComponents/UI/CardContainer";
import ExportButton from "../../../adminCompenents/listComponents/UI/ExportButton";
import AddButton from "../../../adminCompenents/listComponents/UI/AddButton";
import SearchFilterBar from "../../../adminCompenents/listComponents/UI/SearchFilterBar";
import DataTable from "../../../adminCompenents/listComponents/UI/DataTable";
import DataCards from "../../../adminCompenents/listComponents/UI/DataCards";
import Pagination from "../../../adminCompenents/listComponents/UI/Pagination";
import CategoryService from "../../../services/category";
import { showErrorToaster } from "../../../adminCompenents/listComponents/UI/toaster/error";
import { showSuccessToaster } from "../../../adminCompenents/listComponents/UI/toaster/success";
import { exportCSV } from "../../../adminCompenents/utils";
import DrawerForm from "../../../adminCompenents/listComponents/UI/DrawerForm";

const categoryService = new CategoryService();

const CategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [filters, setFilters] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  const filterFields = [
    { name: "title", label: "Titre", placeholder: "Search title..." },
  ];

  // 👉 Responsive detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🔍 Filtering
  const filtered = categories.filter((c) => {
    return (
      c.title?.toLowerCase().includes(search.toLowerCase()) &&
      (filters?.title
        ? c.title.toLowerCase().includes(filters.title.toLowerCase())
        : true)
    );
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ⚡ Load categories
  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await categoryService.getAllCategories();
      setCategories(res?.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Une erreur est survenue.";
      showErrorToaster(errorMessage);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // 🔄 Toggle status
  const handleStatus = async (id) => {
    setIsLoading(true);
    try {
      const response = await categoryService.toggleCategoryStatus(id);
      const updatedCategory = response?.data || response;
      setCategories((prev) =>
        prev.map((c) => (c._id === id ? updatedCategory : c))
      );
      showSuccessToaster("Statut modifié avec succès.");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Une erreur est survenue.";
      showErrorToaster(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 🏷️ Colonnes
  const columns = [
    {
      header: "Image",
      accessor: "image",
      Cell: (value, row) => (
        <img
          src={
            row.image
              ? `${process.env.REACT_APP_API_URL}/categories/${row._id}/image`
              : "/placeholder.png"
          }
          alt={row.title}
          className="category-img"
          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
        />
      ),
    },
    { header: "Titre", accessor: "title" },
    { header: "Slug", accessor: "slug" },
    {
      header: "Status",
      accessor: "status",
      Cell: (value) => (
        <span
          className={`badge ${
            value === "enabled" ? "bg-successstyle" : "bg-dangerstyle"
          }`}>
          {value === "enabled" ? "Active" : "Inactive"}
        </span>
      ),
    },
    { header: "Créé le", accessor: "createdAt" },
  ];
  const apiUrl = `${process.env.REACT_APP_API_URL}`;
  function getImageURL(userId) {
    return `${apiUrl}/categories/${userId}/image`;
  }
  // 🔧 Row actions
  const rowActions = (row) => [
    {
      label: "Edit",
      onClick: () => {
        setEditCategory({
          ...row,
          image: getImageURL(row._id), // ✅ transformer en URL utilisable
        });
        setShowDrawer(true);
      },
    },
    {
      label: row.status === "enabled" ? "Désactiver" : "Activer",
      onClick: async () => handleStatus(row._id),
    },
  ];

  // 📝 Formulaire Drawer
  const fields = [
    {
      name: "title",
      label: "Titre",
      placeholder: "Entrez le titre",
      required: true,
    },
    { name: "slug", label: "Slug", placeholder: "Entrez le slug" },
    { name: "image", label: "Image", type: "file" }, // ✅ Image
  ];

  const handleSubmit = async (formData, editCategory = null) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("slug", formData.slug || "");
      if (formData.image) formDataToSend.append("image", formData.image);

      if (editCategory) {
        const updated = await categoryService.editCategory(
          editCategory._id,
          formDataToSend
        );
        setCategories((prev) =>
          prev.map((c) => (c._id === editCategory._id ? updated.data : c))
        );
        showSuccessToaster("Catégorie modifiée avec succès.");
      } else {
        const newCat = await categoryService.createCategory(formDataToSend);
        setCategories((prev) => [...prev, newCat.data]);
        showSuccessToaster("Catégorie créée avec succès.");
      }
      setShowDrawer(false);
      setEditCategory(null);
    } catch (error) {
      showErrorToaster(
        error.response?.data?.message ||
          "Une erreur est survenue, veuillez réessayer."
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Catégories"
        breadcrumb={[{ label: "Accueil", href: "/" }, { label: "Catégories" }]}
      />

      <CardContainer>
        <div className="d-flex justify-content-between align-items-center mb-3 m-4">
          <h5 className="title-liste">Liste des catégories</h5>
          <div className="d-flex gap-2">
            <ExportButton
              onClick={() => {
                exportCSV(
                  ["title", "slug", "status"],
                  paginated,
                  "categories.csv"
                );
              }}
            />
            <AddButton label="Catégorie" onClick={() => setShowDrawer(true)} />
          </div>
        </div>

        <hr className="hr-table" />
        <SearchFilterBar
          fields={filterFields}
          onSearch={setSearch}
          onFilter={(f) => setFilters(f)}
        />
        <hr className="hr-table" />

        {isMobile ? (
          <DataCards
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        ) : (
          <DataTable
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        )}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </CardContainer>

      <DrawerForm
        show={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setEditCategory(null);
        }}
        title={editCategory ? "Modifier la catégorie" : "Créer une catégorie"}
        fields={fields}
        initialData={editCategory}
        onSubmit={(data) => handleSubmit(data, editCategory)}
      />
    </div>
  );
};

export default CategoryPage;
